import { useState, useEffect, useRef } from "react";
import { useParams } from "wouter";
import { Loader2, CheckCircle2, AlertTriangle, Send, Plus, Minus, X, Search, Bot, Package } from "lucide-react";
import { SECTEURS_ACTIVITE } from "@shared/schema";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdresseStructuree {
  numero: string;
  rue: string;
  code_postal: string;
  ville: string;
}

interface ProduitCatalogue {
  id: string;
  nom: string;
  sku: string | null;
  imageUrl: string | null;
  descriptif: string | null;
}

interface LigneDeclaree {
  produit_id: string;
  sku: string;
  nom: string;
  quantite: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PAYS_OPTIONS = [
  { code: "FR", label: "France", flag: "🇫🇷" },
  { code: "BE", label: "Belgique", flag: "🇧🇪" },
];

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";
const selectCls =
  "w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors appearance-none cursor-pointer";

function AdresseBloc({
  label,
  val,
  onChange,
  optional,
}: {
  label: string;
  val: AdresseStructuree;
  onChange: (v: AdresseStructuree) => void;
  optional?: boolean;
}) {
  const set = (k: keyof AdresseStructuree, v: string) => onChange({ ...val, [k]: v });
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-gray-700">
        {label}
        {optional && <span className="ml-1 text-gray-400 font-normal">(optionnel)</span>}
      </p>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-3">
          <input
            className={inputCls}
            placeholder="N°"
            value={val.numero}
            onChange={e => set("numero", e.target.value)}
          />
        </div>
        <div className="col-span-9">
          <input
            className={inputCls}
            placeholder="Adresse"
            value={val.rue}
            onChange={e => set("rue", e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-4">
          <input
            className={inputCls}
            placeholder="Code postal"
            value={val.code_postal}
            onChange={e => set("code_postal", e.target.value)}
          />
        </div>
        <div className="col-span-8">
          <input
            className={inputCls}
            placeholder="Ville"
            value={val.ville}
            onChange={e => set("ville", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function ProduitPicker({
  title,
  icon: Icon,
  items,
  selected,
  onChange,
  searchPlaceholder,
  showThumb,
}: {
  title: string;
  icon: any;
  items: ProduitCatalogue[];
  selected: LigneDeclaree[];
  onChange: (v: LigneDeclaree[]) => void;
  searchPlaceholder: string;
  showThumb?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = items.filter(
    p =>
      !search ||
      p.nom.toLowerCase().includes(search.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(search.toLowerCase())
  );

  const addItem = (p: ProduitCatalogue) => {
    const exists = selected.find(s => s.produit_id === p.id);
    if (!exists) {
      onChange([...selected, { produit_id: p.id, sku: p.sku || "", nom: p.nom, quantite: 1 }]);
    }
    setSearch("");
    setOpen(false);
  };

  const updateQty = (id: string, delta: number) => {
    onChange(
      selected
        .map(s => s.produit_id === id ? { ...s, quantite: Math.max(1, s.quantite + delta) } : s)
    );
  };

  const setQty = (id: string, v: number) => {
    const q = Math.max(1, isNaN(v) ? 1 : v);
    onChange(selected.map(s => s.produit_id === id ? { ...s, quantite: q } : s));
  };

  const removeItem = (id: string) => onChange(selected.filter(s => s.produit_id !== id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className={labelCls + " mb-0 flex items-center gap-1.5"}>
          <Icon className="w-4 h-4 text-blue-500" />
          {title}
        </p>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Ajouter
        </button>
      </div>

      {open && (
        <div ref={ref} className="border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              autoFocus
              className="flex-1 text-sm text-gray-900 placeholder-gray-400 outline-none"
              placeholder={searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
            {filtered.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-gray-400">Aucun produit trouvé</p>
            )}
            {filtered.map(p => {
              const isSelected = selected.some(s => s.produit_id === p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => addItem(p)}
                  disabled={isSelected}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? "opacity-40 cursor-not-allowed bg-gray-50" : "hover:bg-blue-50"
                  }`}
                >
                  {showThumb && p.imageUrl && (
                    <img src={p.imageUrl} alt={p.nom} className="w-10 h-10 object-contain rounded-md bg-gray-50 border border-gray-100 shrink-0" />
                  )}
                  {showThumb && !p.imageUrl && (
                    <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.nom}</p>
                    {p.sku && <p className="text-xs text-gray-400">SKU : {p.sku}</p>}
                    {showThumb && p.descriptif && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{p.descriptif}</p>
                    )}
                  </div>
                  {isSelected && <span className="text-xs text-green-600 font-medium shrink-0">Ajouté</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="space-y-2">
          {selected.map(s => {
            const prod = items.find(p => p.id === s.produit_id);
            return (
              <div key={s.produit_id} className="flex items-center gap-3 p-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                {showThumb && prod?.imageUrl && (
                  <img src={prod.imageUrl} alt={s.nom} className="w-9 h-9 object-contain rounded-md bg-white border border-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{s.nom}</p>
                  {s.sku && <p className="text-xs text-gray-500">SKU : {s.sku}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => updateQty(s.produit_id, -1)}
                    className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <Minus className="w-3 h-3 text-gray-600" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={s.quantite}
                    onChange={e => setQty(s.produit_id, parseInt(e.target.value))}
                    className="w-10 text-center text-sm font-semibold text-gray-900 border border-gray-200 rounded-lg py-0.5 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => updateQty(s.produit_id, 1)}
                    className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-3 h-3 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(s.produit_id)}
                    className="ml-1 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition-colors"
                  >
                    <X className="w-3 h-3 text-red-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected.length === 0 && !open && (
        <p className="text-sm text-gray-400 italic">Aucun produit sélectionné — cliquez sur « Ajouter »</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const emptyAdresse = (): AdresseStructuree => ({ numero: "", rue: "", code_postal: "", ville: "" });

export default function PublicDeclarationPage() {
  const { token } = useParams<{ token: string }>();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [commercialNom, setCommercialNom] = useState("");
  const [declarationStatut, setDeclarationStatut] = useState("");
  const [catalogue, setCatalogue] = useState<{ robots: ProduitCatalogue[]; accessoires: ProduitCatalogue[] }>({ robots: [], accessoires: [] });

  // Form fields
  const [companyName, setCompanyName] = useState("");
  const [siren, setSiren] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [pays, setPays] = useState("FR");
  const [adresseFacturation, setAdresseFacturation] = useState<AdresseStructuree>(emptyAdresse());
  const [adresseInstallation, setAdresseInstallation] = useState<AdresseStructuree>(emptyAdresse());
  const [secteurCategorie, setSecteurCategorie] = useState("");
  const [secteurActivite, setSecteurActivite] = useState("");
  const [robotsDeclares, setRobotsDeclares] = useState<LigneDeclaree[]>([]);
  const [accessoiresDeclares, setAccessoiresDeclares] = useState<LigneDeclaree[]>([]);
  const [commentaire, setCommentaire] = useState("");

  const sousCategories = SECTEURS_ACTIVITE.find(s => s.categorie === secteurCategorie)?.sousCategories ?? [];

  useEffect(() => {
    if (!token) return;
    // Load context
    fetch(`/api/public/declaration/${token}`)
      .then(async r => {
        const data = await r.json();
        if (!r.ok) { setLoadError(data.message || "Lien invalide"); return; }
        setCommercialNom(data.commercialNom || "");
        setDeclarationStatut(data.declarationStatut || "");
        setCompanyName(data.companyName || "");
        setSiren(data.siren || "");
        setContactName(data.contactName || "");
        setContactPhone(data.contactPhone || "");
        setContactEmail(data.contactEmail || "");
        setPays(data.pays || "FR");
        if (data.adresseFacturation) setAdresseFacturation(data.adresseFacturation);
        if (data.adresseInstallation) setAdresseInstallation(data.adresseInstallation);
        setSecteurCategorie(data.secteurCategorie || "");
        setSecteurActivite(data.secteurActivite || "");
        if (Array.isArray(data.robotsDeclaresJson) && data.robotsDeclaresJson.length) setRobotsDeclares(data.robotsDeclaresJson);
        if (Array.isArray(data.accessoiresDeclares) && data.accessoiresDeclares.length) setAccessoiresDeclares(data.accessoiresDeclares);
        setCommentaire(data.commentaireDeclaration || "");
      })
      .catch(() => setLoadError("Impossible de charger le formulaire"))
      .finally(() => setIsLoading(false));

    // Load catalogue
    fetch(`/api/public/declaration/${token}/catalogue`)
      .then(async r => { if (r.ok) setCatalogue(await r.json()); })
      .catch(() => {});
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !contactName.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch(`/api/public/declaration/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName,
          siren,
          contactName,
          contactPhone,
          contactEmail,
          pays,
          adresseFacturation,
          adresseInstallation: adresseInstallation.rue ? adresseInstallation : null,
          secteurCategorie,
          secteurActivite,
          robotsDeclaresJson: robotsDeclares,
          accessoiresDeclares,
          commentaireDeclaration: commentaire,
        }),
      });
      const data = await r.json();
      if (!r.ok) { setSubmitError(data.message || "Erreur lors de l'envoi"); return; }
      setSubmitted(true);
    } catch {
      setSubmitError("Erreur réseau. Vérifiez votre connexion et réessayez.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide ou expiré</h1>
          <p className="text-gray-500 text-sm">{loadError}</p>
          <p className="text-gray-400 text-xs mt-4">Contactez votre commercial Jobotto pour obtenir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Vente déclarée !</h1>
          <p className="text-gray-600 mb-4">
            Merci pour votre déclaration. L'équipe Jobotto a été notifiée et va prendre en charge le dossier.
          </p>
          <p className="text-sm text-gray-400">Le client recevra un mail de bienvenue dès que la fiche aura été validée par nos équipes.</p>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Send className="w-3.5 h-3.5" />
            Déclaration de vente
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Déclarez votre vente</h1>
          {commercialNom && (
            <p className="text-gray-500 mt-2">
              Bonjour <strong className="text-gray-700">{commercialNom}</strong>, merci de remplir ce formulaire.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-7">

          {/* Section société */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Informations société</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Société *</label>
                <input
                  data-testid="input-decl-company"
                  required
                  className={inputCls}
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="Raison sociale"
                />
              </div>
              <div>
                <label className={labelCls}>SIREN</label>
                <input
                  data-testid="input-decl-siren"
                  className={inputCls}
                  value={siren}
                  onChange={e => setSiren(e.target.value)}
                  placeholder="123 456 789"
                />
              </div>
            </div>
          </div>

          {/* Section contact */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Contact client</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-1">
                <label className={labelCls}>Nom du contact *</label>
                <input
                  data-testid="input-decl-contact"
                  required
                  className={inputCls}
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  placeholder="Prénom Nom"
                />
              </div>
              <div>
                <label className={labelCls}>Téléphone</label>
                <input
                  data-testid="input-decl-phone"
                  type="tel"
                  className={inputCls}
                  value={contactPhone}
                  onChange={e => setContactPhone(e.target.value)}
                  placeholder="06 00 00 00 00"
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  data-testid="input-decl-email"
                  type="email"
                  className={inputCls}
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  placeholder="contact@societe.fr"
                />
              </div>
            </div>
          </div>

          {/* Section adresses */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Adresses</h2>

            {/* Pays */}
            <div className="mb-4">
              <label className={labelCls}>Pays</label>
              <div className="flex gap-2">
                {PAYS_OPTIONS.map(p => (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setPays(p.code)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      pays === p.code
                        ? "bg-blue-50 border-blue-400 text-blue-700 shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-base">{p.flag}</span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              <AdresseBloc
                label="Adresse de facturation"
                val={adresseFacturation}
                onChange={setAdresseFacturation}
              />
              <AdresseBloc
                label="Site à installer"
                val={adresseInstallation}
                onChange={setAdresseInstallation}
                optional
              />
            </div>
          </div>

          {/* Section secteur */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Secteur d'activité</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Catégorie</label>
                <div className="relative">
                  <select
                    data-testid="select-secteur-cat"
                    className={selectCls}
                    value={secteurCategorie}
                    onChange={e => { setSecteurCategorie(e.target.value); setSecteurActivite(""); }}
                  >
                    <option value="">— Choisir une catégorie</option>
                    {SECTEURS_ACTIVITE.map(s => (
                      <option key={s.categorie} value={s.categorie}>{s.categorie}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">▾</div>
                </div>
              </div>
              <div>
                <label className={labelCls}>Sous-catégorie</label>
                <div className="relative">
                  <select
                    data-testid="select-secteur-sous"
                    className={`${selectCls} ${!secteurCategorie ? "opacity-50" : ""}`}
                    value={secteurActivite}
                    onChange={e => setSecteurActivite(e.target.value)}
                    disabled={!secteurCategorie}
                  >
                    <option value="">— Choisir</option>
                    {sousCategories.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">▾</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section robots */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Produits</h2>
            <div className="space-y-6">
              <ProduitPicker
                title="Robot(s) vendu(s)"
                icon={Bot}
                items={catalogue.robots}
                selected={robotsDeclares}
                onChange={setRobotsDeclares}
                searchPlaceholder="Rechercher un robot…"
              />
              <div className="border-t border-gray-100 pt-5">
                <ProduitPicker
                  title="Accessoires"
                  icon={Package}
                  items={catalogue.accessoires}
                  selected={accessoiresDeclares}
                  onChange={setAccessoiresDeclares}
                  searchPlaceholder="Rechercher un accessoire…"
                  showThumb
                />
              </div>
            </div>
          </div>

          {/* Commentaire */}
          <div>
            <h2 className="text-base font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">Informations complémentaires</h2>
            <label className={labelCls}>Particularités / Notes</label>
            <textarea
              data-testid="input-decl-comment"
              className={`${inputCls} resize-none`}
              rows={4}
              value={commentaire}
              onChange={e => setCommentaire(e.target.value)}
              placeholder="Contraintes d'accès, horaires, informations spécifiques…"
            />
          </div>

          {/* Error */}
          {submitError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {submitError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            data-testid="button-decl-submit"
            disabled={isSubmitting || !companyName.trim() || !contactName.trim()}
            className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Envoi en cours…</>
            ) : (
              <><Send className="w-4 h-4" />Envoyer la déclaration</>
            )}
          </button>

          <p className="text-xs text-center text-gray-400">
            Ces informations sont transmises directement à l'équipe Jobotto en toute sécurité.
          </p>
        </form>
      </div>
    </div>
  );
}
